import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import { useAuth } from "../Auth/AuthContext";
import { FaArrowLeft, FaBuilding, FaSignInAlt, FaPause, FaPlay, FaUsers } from "react-icons/fa";

const ROLE_OPTIONS = ["superadmin", "org_admin", "user"];
const ROLE_BADGE = { superadmin: "badge-error", org_admin: "badge-primary", user: "badge-ghost" };

export default function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrg = async () => {
    try {
      const { data } = await api.get(`/admin/organizations/${id}`);
      setOrg(data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Organisation introuvable");
      navigate("/admin/organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrg(); /* eslint-disable-next-line */ }, [id]);

  const toggleStatus = async () => {
    const next = org.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${next === "suspended" ? "Suspendre" : "Réactiver"} « ${org.name} » ?`)) return;
    try {
      await api.patch(`/admin/organizations/${org.id}/status`, { status: next });
      setOrg((o) => ({ ...o, status: next }));
      toast.success(`« ${org.name} » ${next === "suspended" ? "suspendue" : "réactivée"}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec");
    }
  };

  const enter = async () => {
    try {
      const { data } = await api.post(`/admin/organizations/${org.id}/impersonate`);
      impersonate(data.token, { id: org.id, name: org.name, slug: org.slug });
      toast.success(`Vous consultez « ${org.name} »`);
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Impossible d'entrer");
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      setOrg((o) => ({ ...o, users: o.users.map((x) => (x.id === u.id ? { ...x, role } : x)) }));
      toast.success(`${u.username} → ${role}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec");
    }
  };

  if (loading) return <div className="flex justify-center h-48 items-center"><span className="loading loading-spinner loading-lg text-primary" /></div>;
  if (!org) return null;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/admin/organizations")} className="btn btn-ghost btn-sm gap-2 -ml-2">
        <FaArrowLeft /> Organisations
      </button>

      {/* En-tête org */}
      <div className="rounded-2xl border border-base-200 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-2xl">
            <FaBuilding />
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {org.name}
              <span className={`badge badge-sm ${org.status === "active" ? "badge-success" : "badge-warning"}`}>
                {org.status === "active" ? "active" : "suspendue"}
              </span>
            </h1>
            <div className="text-sm text-base-content/50">
              #{org.id} · slug <code>{org.slug}</code>
              {org.created_at && <> · créée {new Date(org.created_at).toLocaleDateString("fr-FR")}</>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {org.id !== 0 && (
            <button onClick={enter} className="btn btn-primary btn-sm gap-2"><FaSignInAlt /> Entrer</button>
          )}
          {org.id !== 0 && (
            <button onClick={toggleStatus} className={`btn btn-sm gap-2 ${org.status === "active" ? "btn-ghost text-warning" : "btn-ghost text-success"}`}>
              {org.status === "active" ? <><FaPause /> Suspendre</> : <><FaPlay /> Réactiver</>}
            </button>
          )}
        </div>
      </div>

      {/* Utilisateurs de l'org */}
      <div className="space-y-3">
        <h2 className="text-sm font-bold tracking-wide text-base-content/50 uppercase flex items-center gap-2">
          <FaUsers /> Utilisateurs ({org.users?.length || 0})
        </h2>
        <div className="overflow-x-auto rounded-xl border border-base-300 shadow-sm">
          <table className="table table-zebra">
            <thead>
              <tr className="bg-base-200 text-base-content/70 text-xs uppercase tracking-wide">
                <th>Username</th><th>Email</th><th>Rôle</th><th>Changer</th>
              </tr>
            </thead>
            <tbody>
              {(org.users || []).length === 0 && (
                <tr><td colSpan={4} className="text-center text-base-content/40 py-6">Aucun utilisateur.</td></tr>
              )}
              {(org.users || []).map((u) => (
                <tr key={u.id}>
                  <td className="font-medium">{u.username}</td>
                  <td className="text-base-content/50">{u.email || "—"}</td>
                  <td><span className={`badge badge-sm ${ROLE_BADGE[u.role] || "badge-ghost"}`}>{u.role}</span></td>
                  <td>
                    <select className="select select-bordered select-xs" value={u.role} onChange={(e) => changeRole(u, e.target.value)}>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-base-content/40">
          Pour ajouter/inviter des membres, utilisez « Entrer » puis la page Utilisateurs de l'org.
        </p>
      </div>
    </div>
  );
}
